<template>
<i-dropdown>
  <template v-slot:trigger="{toggle, attrs}">
    <a href="#"
      :title="LANG.moreOptionsLabel"
      class="ph1 outline-none inline-block trigger"
      @click.prevent="toggle"
      v-bind="attrs"
    >
      <i aria-hidden="true" :title="LANG.moreOptionsLabel" class="fas fa-ellipsis-h" name="ellipsis-h"></i>
      <span class="sr-only">{{LANG.moreOptionsLabel}}</span>
    </a>
  </template>

  <template v-slot:default="{close}">
    <ul class="dropdown-menu i-dropdown-menu block pull-right" style="margin-top: 2px;">
      <li v-for="{ label, href, icon, enabled } in primaryMenuItems" :key="label" role="presentation" :aria-label="label">
         <a :href="href" :title="label" :class="{ 'i-state-disabled': !enabled }" @click.stop>
            <i class="fa5 fas" :class="`fa-${icon}`" :name="icon"></i>
            <span>{{ label }}</span>
         </a>
      </li>
      <hr class="divider" role="separator">
      <li v-for="{ label, href, icon, enabled } in secondaryMenuItems" :key="label" role="presentation" :aria-label="label">
         <a :href="href" :title="label" :class="{ 'i-state-disabled': !enabled }" @click.stop>
            <i class="fa5 fas" :class="`fa-${icon}`" :name="icon"></i>
            <span>{{ label }}</span>
         </a>
      </li>
      <template v-if="expandable">
        <hr class="divider" role="separator" />
        <li role="presentation" :aria-label="LANG.expandCategories">
          <a
            href="#"
            :title="LANG.expandCategories"
            @click.prevent="() => { expandItem({ expandDescendants: true }); close(); }"
            @keydown.enter.stop.prevent="() => { expandItem({ expandDescendants: true }); close(); }"
          >
            <i-icon
              class="fa5 fas"
              icon="chevron-down"
            />
            <span>{{ LANG.expandCategories }}</span>
          </a>
        </li>
      </template>
    </ul>
  </template>
</i-dropdown>
</template>

<script>
import globals from '@/globals';
import { mapState, mapGetters } from 'vuex';
import { BrowserMode, FinishController } from '@/store/modules/entity_browser/config';
import { treeNodeActions } from '@/../imperia.spa.config';

const { uri } = globals.I.Util;

export default {
  props: {
    node: {
      type: Object,
      required: true,
    },
    expandable: {
      type: Boolean,
    },
    expandItem: {
      type: Function,
    },
  },
  computed: {
    ...mapState(['LANG', 'browserMode']),
    ...mapGetters(['checkACL']),
    primaryMenuItemsByMode() {
      return {
        [BrowserMode.DOC]: [
          {
            label: this.LANG.createNewDocument,
            icon: 'file-plus',
            href: uri('/document/create', { newdoc_cat: this.node.id }),
            enabled: this.checkACL('/document/create') && this.node.can_create_documents,
          },
        ],
        [BrowserMode.MAM]: [
          {
            label: this.LANG.upload,
            icon: 'file-upload',
            href: uri('/document/media/multi_upload', { cat: this.node.id }),
            enabled: this.checkACL('/document/media/multi_upload') && this.node.can_create_documents,
          },
          {
            label: this.LANG.import,
            icon: 'download',
            href: uri('/document/media/spool_import', { category: this.node.id }),
            enabled: this.checkACL('/document/media/spool_import') && this.node.can_create_documents,
          },
        ],
      };
    },
    primaryMenuItems() {
      return this.primaryMenuItemsByMode[this.browserMode] || [];
    },
    secondaryMenuItems() {
      return [
        {
          label: this.LANG.createCategory,
          icon: 'folder-plus',
          href: uri('/structure/new_edit', { new: this.node.id, backto: uri(FinishController[this.browserMode]) }),
          enabled: this.checkACL('/structure/new_edit') && this.node.can_edit,
        },
        {
          label: this.LANG.editCategory,
          icon: 'folder',
          href: uri('/structure/new_edit', { cat: this.node.id, backto: uri(FinishController[this.browserMode]) }),
          enabled: this.checkACL('/structure/new_edit') && this.node.can_edit,
        },
        {
          label: this.LANG.archive,
          icon: 'archive',
          href: uri('/document/archive', { category: this.node.id }),
          enabled: this.checkACL('/document/archive'),
        },
        {
          label: this.LANG.deleteCategory,
          icon: 'folder-times',
          href: uri(`/structure/categories/delete${this.node.id}`, { backto: uri(FinishController[this.browserMode]) }),
          enabled: this.checkACL('/structure/categories/delete') && this.node.can_edit,
        },
        ...treeNodeActions({ node: this.node, uri }),
      ];
    },
  },
};
</script>

<style scoped lang='scss'>
a.trigger {
  outline: 2px solid transparent;
  outline-offset: 2px;
  color: $blue;

  &:hover {
    background-color: $ibutton-bg-hover-color-light;
  }

  &:active {
    background-color: $i-list-selected-bg;
  }
}
</style>
